import { AdminApi } from "../domain/service";

export interface LocationDto {
  latitude: number;
  longitude: number;
}

export interface Address {
  address: string;
  fullAddress: string | null;
  lat: number;
  lng: number;
}

interface ReverseGeocodingResponse {
  address: string;
  fullAddress: string | null;
}

interface ReverseGeocodingBulkItem {
  lat: number;
  lng: number;
  address: string;
  fullAddress: string | null;
}

interface ReverseGeocodingBulkResponse {
  items: ReverseGeocodingBulkItem[];
}

export async function getAddress(latlng: LocationDto | null): Promise<Address | null> {
  if (!latlng) return null;

  try {
    const { data } = await AdminApi.get<ReverseGeocodingResponse>(
      `utilities/geocording/reverse?lat=${latlng.latitude}&lng=${latlng.longitude}`,
    );
    return {
      address: data.address,
      fullAddress: data.fullAddress,
      lat: latlng.latitude,
      lng: latlng.longitude,
    };
  } catch (error) {
    console.error("Error in getAddress:", error);
    return null;
  }
}

/**
 * 좌표 배열을 admin-api bulk 엔드포인트로 reverse-geocoding.
 *
 * 단건 N회 호출 대비 admin-api Tomcat worker 점유와 RPS spike를 크게 줄인다.
 * 입력 순서대로 응답 배열을 돌려준다. cache miss는 빈 값(`address=""`, `fullAddress=null`)으로 채워진다.
 *
 * 한 번에 보내는 좌표 수가 너무 많으면 admin-api 한 요청이 한 worker를 오래 점유하므로
 * `CHUNK_SIZE` 단위로 잘라 순차 호출한다 (호출자는 청크 인지 불필요).
 */
const BULK_CHUNK_SIZE = 1000;

async function postAddressBulkChunk(latlngs: LocationDto[], throwOnError = false): Promise<(Address | null)[]> {
  try {
    const { data } = await AdminApi.post<ReverseGeocodingBulkResponse>(
      "utilities/geocording/reverse/bulk",
      {
        coordinates: latlngs.map((c) => ({ lat: c.latitude, lng: c.longitude })),
      },
    );
    return data.items.map((item) => ({
      address: item.address,
      fullAddress: item.fullAddress,
      lat: item.lat,
      lng: item.lng,
    }));
  } catch (error) {
    console.error("Error in getAddressBulk chunk:", error);
    if (throwOnError) throw error;
    return latlngs.map(() => null);
  }
}

export async function getAddressBulk(latlngs: LocationDto[], throwOnError = false): Promise<(Address | null)[]> {
  if (latlngs.length === 0) return [];

  const results: (Address | null)[] = [];
  for (let i = 0; i < latlngs.length; i += BULK_CHUNK_SIZE) {
    const chunk = latlngs.slice(i, i + BULK_CHUNK_SIZE);
    const chunkResults = await postAddressBulkChunk(chunk, throwOnError);
    results.push(...chunkResults);
  }
  return results;
}
